import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { addRecent } from "@/hooks/usePrefs";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { RecordPanels } from "@/components/shared/RecordPanels";
import { DataTable } from "@/components/shared/DataTable";
import { StatusPill } from "@/components/shared/StatusPill";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, PackagePlus, PackageMinus, ArrowLeftRight, SlidersHorizontal, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { has } = useAtlas();
  const [item, setItem] = useState(null);
  const [locations, setLocations] = useState([]);
  const [movements, setMovements] = useState([]);
  const [action, setAction] = useState(null); // receive|remove|transfer|adjust
  const canEdit = has("inventory.items.edit");
  const canAdjust = has("inventory.adjust");

  const load = useCallback(() => {
    api.get(`/inventory/items/${id}`).then((r) => { setItem(r.data); addRecent({ type: "item", id, label: r.data.name, link: `/inventory/items/${id}` }); }).catch(() => {});
    api.get("/inventory/movements", { params: { item_id: id } }).then((r) => setMovements(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); api.get("/inventory/locations").then((r) => setLocations(r.data)).catch(() => {}); }, [load]);

  const locName = (lid) => locations.find((l) => l.id === lid)?.name || lid;

  if (!item) return <div className="text-slate-400">Loading…</div>;

  const moveCols = [
    { key: "created_at", header: "When", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "type", header: "Type", render: (r) => <StatusPill value={r.type} /> },
    { key: "location_id", header: "Location", render: (r) => locName(r.location_id) },
    { key: "quantity_change", header: "Change", align: "right", mono: true, render: (r) => <span className={r.quantity_change >= 0 ? "text-emerald-600" : "text-red-600"}>{r.quantity_change > 0 ? "+" : ""}{r.quantity_change}</span> },
    { key: "balance_after", header: "Balance", align: "right", mono: true },
    { key: "actor_name", header: "By", render: (r) => r.actor_name || "—" },
  ];

  return (
    <div>
      <button onClick={() => navigate("/inventory/items")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Items</button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h1 className="text-xl font-heading font-semibold text-slate-900">{item.name}</h1>
            <p className="text-sm text-slate-500 font-mono">{item.sku}</p>
            {item.description && <p className="text-sm text-slate-600 mt-2">{item.description}</p>}
            {item.vendor_name && <div className="flex items-center gap-2 text-sm text-slate-600 mt-3"><Building2 className="w-4 h-4 text-slate-400" />Vendor: {item.vendor_name}</div>}
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-3xl font-heading font-semibold text-slate-900 tabular">{item.total_quantity}</div>
              <div className="text-sm text-slate-500">total on hand</div>
            </div>
            <div className="mt-3 space-y-1">
              {(item.balances || []).map((b) => <div key={b.id} className="flex justify-between text-sm"><span className="text-slate-600">{locName(b.location_id)}</span><span className="font-mono tabular font-medium">{b.quantity}</span></div>)}
            </div>
          </div>

          {(canEdit || canAdjust) && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-2 gap-2">
              {canEdit && <Button variant="outline" size="sm" onClick={() => setAction("receive")} data-testid="inventory-receive-button"><PackagePlus className="w-4 h-4 mr-1" /> Receive</Button>}
              {canEdit && <Button variant="outline" size="sm" onClick={() => setAction("remove")} data-testid="inventory-remove-button"><PackageMinus className="w-4 h-4 mr-1" /> Remove</Button>}
              {canEdit && <Button variant="outline" size="sm" onClick={() => setAction("transfer")} data-testid="inventory-transfer-button"><ArrowLeftRight className="w-4 h-4 mr-1" /> Transfer</Button>}
              {canAdjust && <Button variant="outline" size="sm" onClick={() => setAction("adjust")} data-testid="inventory-adjust-stock-button"><SlidersHorizontal className="w-4 h-4 mr-1" /> Adjust</Button>}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">Movement history</h3>
            <DataTable testid="inventory-movements-table" columns={moveCols} rows={movements} empty="No movements recorded" />
          </div>
          <RecordPanels relatedType="item" relatedId={item.id} relatedLabel={item.name} sourceApp="inventory" />
        </div>
      </div>

      <StockActionModal action={action} onClose={() => setAction(null)} item={item} locations={locations} onDone={() => { setAction(null); load(); }} />
    </div>
  );
}

function StockActionModal({ action, onClose, item, locations, onDone }) {
  const [locationId, setLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (action) { setLocationId(locations[0]?.id || ""); setToLocationId(locations[1]?.id || ""); setQty(""); setReason(""); setNote(""); } }, [action]);

  const titles = { receive: "Receive stock", remove: "Remove stock", transfer: "Transfer stock", adjust: "Adjust inventory" };

  const submit = async () => {
    setSaving(true);
    try {
      if (action === "receive") await api.post("/inventory/receive", { item_id: item.id, location_id: locationId, quantity: Number(qty), note });
      else if (action === "remove") await api.post("/inventory/remove", { item_id: item.id, location_id: locationId, quantity: Number(qty), note });
      else if (action === "transfer") await api.post("/inventory/transfer", { item_id: item.id, from_location_id: locationId, to_location_id: toLocationId, quantity: Number(qty), note });
      else if (action === "adjust") await api.post("/inventory/adjust", { item_id: item.id, location_id: locationId, new_quantity: Number(qty), reason, note });
      toast.success("Inventory updated");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stock-action-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">{titles[action]} · {item?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>{action === "transfer" ? "From location" : "Location"}</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger data-testid="stock-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
              <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {action === "transfer" && (
            <div className="space-y-1.5">
              <Label>To location</Label>
              <Select value={toLocationId} onValueChange={setToLocationId}>
                <SelectTrigger data-testid="stock-to-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{action === "adjust" ? "New counted quantity" : "Quantity"}</Label>
            <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="stock-qty-input" />
          </div>
          {action === "adjust" && (
            <div className="space-y-1.5"><Label>Reason (required)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="stock-reason-input" placeholder="e.g. cycle count correction" /></div>
          )}
          <div className="space-y-1.5"><Label>Note (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !qty || (action === "adjust" && !reason)} data-testid="stock-action-submit">{saving ? "Saving…" : "Confirm"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
